import {
  NUTRITION_RATE_LOCKED_AMBIGUOUS_STEPS_MESSAGE,
  NUTRITION_RATE_LOCKED_OPEN_PLAN_MESSAGE,
  NUTRITION_RATE_LOCKED_UNSAVED_MESSAGE,
  TASK_SAVE_ERROR_MESSAGE,
} from 'constants/messageConstants';
import { NUTRITION_FAILED_SAVE_DESCRIPTION } from 'constants/uiConstants';
import { useUpsertInputTask } from 'hooks/inputs/useUpsertInputTask';
import type { IInput } from 'interfaces/IInput';
import type { IInputStep } from 'interfaces/IInputStep';
import type { IInputTask } from 'interfaces/IInputTask';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import BrowserStorageUtils from 'utils/BrowserStorageUtils';
import { toast } from 'utils/toast';
import { getApiErrorMessage } from 'utils/UtilityMethods';
import {
  nutritionSaveFailed,
  nutritionSaveStarted,
  nutritionSaveSucceeded,
  type NutritionFailedSave,
} from '../../../../../redux/slices/InsightsSlice';
import type { RootState } from '../../../../../redux/Store';
import { hasDuplicateStepIds } from '../../Tasks/hasDuplicateStepIds';
import type { NutritionProductLine } from './computeNutritionAudit';
import type { TipText } from './TipContent';

export interface NutritionRateEdit {
  /** False in Actual mode — Actual values are entered elsewhere, never here. */
  canEdit: boolean;
  /** Why this line's rate can't be changed here, or null when it can. */
  lockReason: (line: NutritionProductLine) => TipText | null;
  /** True while a save for this line's task is in flight. */
  isSaving: (line: NutritionProductLine) => boolean;
  /** The rate to show: the one just typed while its save is in flight, else the plan's. */
  displayRate: (line: NutritionProductLine) => number;
  commitRate: (line: NutritionProductLine, rate: number) => void;
}

/** What a save needs: which step on which task of which plan. */
type RateTarget = Pick<
  NutritionFailedSave,
  'key' | 'inputId' | 'taskId' | 'stepId' | 'description'
>;

const taskKeyOf = (target: Pick<RateTarget, 'inputId' | 'taskId'>) =>
  `${target.inputId}:${target.taskId}`;

/**
 * Rate editing for the Nutrition lens (OP-2508 stage B). A change saves the
 * line's whole task straight away, built from the latest cached plan, with
 * only that step's rate changed — the same shape the Tasks grid sends.
 *
 * Each save reports to the workspace's save status label (Saving…, Saved, or
 * an error with Retry) via insightsReducer.nutritionSave. A failed change is
 * kept there until a retry succeeds; Retry in the label bumps retryRequest,
 * and this hook resends every failed change.
 *
 * A plan is locked here while it's open in Planning or has unsaved edits
 * there: the Planning draft doesn't pick up saves made elsewhere, so its next
 * task save would put the old rate back. A task is also locked while its own
 * save is in flight (a second save would be built without the first) and
 * when its step ids are ambiguous.
 */
export const useNutritionRateEdit = (
  inputs: IInput[],
  canEdit: boolean
): NutritionRateEdit => {
  const activeOrganisationId = useSelector(
    (state: RootState) => state.activeOrganisationReducer.organisationId
  );
  const selectedInputId = useSelector(
    (state: RootState) => state.inputReducer.selectedInputId
  );
  const { failed, retryRequest } = useSelector(
    (state: RootState) => state.insightsReducer.nutritionSave
  );
  const dispatch = useDispatch();
  const { mutate } = useUpsertInputTask({ activeOrganisationId });
  const [savingTaskKeys, setSavingTaskKeys] = useState<Set<string>>(
    () => new Set()
  );
  // The rate just typed, per product line, held from the moment the field is
  // left until its save settles — so the box never shows the old value in
  // between, however long the cache takes to catch up.
  const [pendingRates, setPendingRates] = useState<Map<string, number>>(
    () => new Map()
  );
  const setPendingRate = useCallback((key: string, rate: number | null) => {
    setPendingRates((prev) => {
      const next = new Map(prev);
      if (rate == null) next.delete(key);
      else next.set(key, rate);
      return next;
    });
  }, []);

  const inputsById = useMemo(
    () => new Map(inputs.map((input) => [input.id, input])),
    [inputs]
  );

  const findTask = useCallback(
    (target: Pick<RateTarget, 'inputId' | 'taskId'>): IInputTask | undefined =>
      inputsById
        .get(target.inputId)
        ?.tasks?.find((task) => task.taskId === target.taskId),
    [inputsById]
  );

  const lockReasonFor = useCallback(
    (target: Pick<RateTarget, 'inputId' | 'taskId'>): TipText | null => {
      if (target.inputId === selectedInputId) {
        return NUTRITION_RATE_LOCKED_OPEN_PLAN_MESSAGE;
      }
      if (BrowserStorageUtils.hasEditedInput(target.inputId)) {
        return NUTRITION_RATE_LOCKED_UNSAVED_MESSAGE;
      }
      const task = findTask(target);
      if (task && hasDuplicateStepIds(task.steps)) {
        return NUTRITION_RATE_LOCKED_AMBIGUOUS_STEPS_MESSAGE;
      }
      return null;
    },
    [findTask, selectedInputId]
  );

  const isSaving = useCallback(
    (line: NutritionProductLine) => savingTaskKeys.has(taskKeyOf(line)),
    [savingTaskKeys]
  );

  const setSaving = useCallback((key: string, saving: boolean) => {
    setSavingTaskKeys((prev) => {
      const next = new Set(prev);
      if (saving) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  /** Sends one rate change; false when it can't be sent right now (locked, busy or gone). */
  const saveRate = useCallback(
    (target: RateTarget, rate: number): boolean => {
      if (lockReasonFor(target)) return false;
      const taskKey = taskKeyOf(target);
      if (savingTaskKeys.has(taskKey)) return false;
      const task = findTask(target);
      if (!task) return false;

      const updatedTask: IInputTask = {
        ...task,
        steps: task.steps.map((step) =>
          step.stepId === target.stepId
            ? ({ ...step, rate } as IInputStep)
            : step
        ),
      };

      setSaving(taskKey, true);
      setPendingRate(target.key, rate);
      dispatch(nutritionSaveStarted());
      mutate(
        { inputId: target.inputId, task: updatedTask },
        {
          onSuccess: () => dispatch(nutritionSaveSucceeded(target.key)),
          onError: (error) => {
            const reason = getApiErrorMessage(error, TASK_SAVE_ERROR_MESSAGE);
            dispatch(nutritionSaveFailed({ ...target, rate, reason }));
            toast.error(reason);
          },
          onSettled: () => {
            setSaving(taskKey, false);
            setPendingRate(target.key, null);
          },
        }
      );
      return true;
    },
    [
      dispatch,
      findTask,
      lockReasonFor,
      mutate,
      savingTaskKeys,
      setPendingRate,
      setSaving,
    ]
  );

  const commitRate = useCallback(
    (line: NutritionProductLine, rate: number) => {
      if (!canEdit || rate < 0 || rate === line.rate) return;
      saveRate(
        {
          key: line.key,
          inputId: line.inputId,
          taskId: line.taskId,
          stepId: line.stepId,
          description: NUTRITION_FAILED_SAVE_DESCRIPTION(
            line.productName,
            line.location
          ),
        },
        rate
      );
    },
    [canEdit, saveRate]
  );

  // Retry in the save status label: resend failed changes. Each save sends
  // the whole application, so only one per application goes per retry — two
  // at once would each be built without the other. Anything not sent (a
  // second change on the same application, or a plan now open in Planning)
  // stays listed for the next retry.
  const lastRetryRef = useRef(retryRequest);
  useEffect(() => {
    if (retryRequest === lastRetryRef.current) return;
    lastRetryRef.current = retryRequest;
    const sentTasks = new Set<string>();
    failed.forEach((item) => {
      const taskKey = taskKeyOf(item);
      if (sentTasks.has(taskKey)) return;
      if (saveRate(item, item.rate)) sentTasks.add(taskKey);
    });
  }, [failed, retryRequest, saveRate]);

  const displayRate = useCallback(
    (line: NutritionProductLine) => pendingRates.get(line.key) ?? line.rate,
    [pendingRates]
  );

  return {
    canEdit,
    lockReason: lockReasonFor,
    isSaving,
    displayRate,
    commitRate,
  };
};
